import { CACHE_CONFIG } from '../config/reactQuery';
import { MessageCache, OfflineMessageQueue } from './chatPersistence';
import { clearAuthIntent } from '@utils/authIntent';
import { forgetAllGuestTokens } from '@lib/livePolls/guestToken';
import { TAKEOVER_STORAGE_PREFIXES, forgetAllTakeovers } from '@lib/livePolls/liveTakeover';
import {
  FIRST_SEND_ADDRESS_KEY,
  FIRST_SEND_SENT_TO_ANOTHER_KEY,
  FIRST_SEND_TURNED_DOWN_KEY,
} from '@lib/magicLink/firstSendAddress';
import { AUTH_LINK_EMAIL_KEY } from '@lib/magicLink/redemptionEmail';
import { AUTH_LINK_PARAMS_STASH_KEY } from '@lib/magicLink/authLinkParams';
import { SIGNED_IN_CUE_KEY } from '@lib/magicLink/signedInCue';
import { SILENT_REAUTH_KEY } from '@utils/auth';
import { USER_INITIATED_SIGNIN_KEY } from '@utils/crossTabAuthSync/authFlowFlags';
import { clearContinuationOnAccountSwitch } from '@lib/magicLink/continuation';
import { MAGIC_LINK_MIRROR_KEYS, magicLinkMirrorKeysOfKind } from '@utils/magicLinkMirror';
import {
  PENDING_QUESTION_DEFERRED_KEY,
  PENDING_QUESTION_TAB_KEYS,
} from '@utils/pendingQuestionClaim';
import { INTENTS_STORAGE_KEY, clearIntents } from '@lib/overlay/intents';
import { forgetUnpersisted } from '@utils/storedValue';
import type { QuestionDraft } from '@stores/createQuestionDraftStore';
import { logger } from './logger';

const LOCAL_STORAGE_KEYS = [
  'auth-storage',
  'meetball-app-store',
  'meetball_redirect_path',
  // Durable space-invitation token (#673). Identity-scoped on read, so a leak is already
  // impossible, but a capability that outlives the page has no business surviving a sign-out.
  'meetball_invitation_capture',
  // AIDEV-NOTE [MEETBALL magic-link]: the cross-tab mirrors (redirect path +
  // auth intent). Spread rather than listed so a future third mirror is cleaned
  // on sign-out by construction. These carry ONE user's destination and pending
  // action for 30 minutes: the redirect mirror used to survive sign-out
  // entirely, so user A's private destination (invite capability and all) was
  // still there for user B to be navigated to on the same browser profile.
  ...Object.values(MAGIC_LINK_MIRROR_KEYS),
];
// AIDEV-NOTE: meetball_device_token is intentionally NOT included here
// It persists across logouts to enable guest question claiming on re-login
// See src/utils/deviceToken.ts for device token management
//
// AIDEV-NOTE: meetball_push_token is intentionally NOT included here
// It must survive until unregisterPushToken() reads it during logout
// See src/hooks/usePushNotifications.ts for push token lifecycle
//
// AIDEV-NOTE: an `onboarding:` prefix is intentionally NOT included here either.
// Those are device-level first-run records (src/utils/onboardingProgress.ts):
// `onboarding:welcome-screen:default` gates the /welcome entry redirect,
// `onboarding:space-welcome:*` gates the legacy Spaces-tab drawer — a SEPARATE record by design,
//   the two surfaces never suppress each other,
// `onboarding:space-welcome:<variant>:<spaceId>` gates the per-space drawers, and
// `onboarding:event-going-welcome:*` gates the first-RSVP drawer. Clearing them on sign-out
// would replay the entire onboarding for the SAME user on their next sign-in. Shared-device
// leakage of the once-ever celebration guards is fixed at the source instead: CelebrationHost
// writes them with the user id as contextId.

// AIDEV-NOTE: Logto SDK storage prefixes for complete auth state cleanup
const LOGTO_STORAGE_PREFIXES = ['logto:', 'logto.'];

const LOCAL_STORAGE_PREFIXES = [
  'web_push_dismissed_',
  // Residue, not live state: private profile notes, written per profile until v4.4.0
  // (a124b9dfa) removed the writer. Devices that haven't signed out since still hold
  // them, and this sweep is the only thing that removes them.
  'profile-notes-',
  // Live poll takeovers (TECH-867 L14/L14b): per person, they name a poll's author and its (maybe private) Space.
  // Each sweep also calls `forgetAllTakeovers` (memory fallback + telling a LIVE bar on screen).
  ...TAKEOVER_STORAGE_PREFIXES,
];
const SESSION_STORAGE_KEYS = [
  'create-question-draft',
  'create-space-draft',
  'setup-space-draft',
  'meetball_auth_intent', // Consolidated auth intent (post-login action)
  'meetball_redirect_path', // Post-auth redirect target (also in LOCAL_STORAGE_KEYS for legacy)
  'meetball_redirect_timestamp', // Redirect path expiry timestamp
  // The pending-question claim, ALL of it: the flag, the questions it would publish, their
  // provenance and a deferred scope only a gate may spend. Spread rather than listed so a fifth
  // key is swept on sign-out by construction -- none of them may outlive a sign-out, so there is
  // no judgement here to write out by hand. Owned by `@utils/pendingQuestionClaim`. On native
  // the claim also has a durable pair in Capacitor Preferences, which no walk in this file
  // reaches: `executeSignOut` and `LogoutCallback` clear it (`clearNativePendingQuestionClaim`).
  ...PENDING_QUESTION_TAB_KEYS,
  INTENTS_STORAGE_KEY, // Overlay intent channel (lib/overlay/intents.ts)
  'mb:onboarding-skipped-session', // "Continue as guest" decline (screens/Onboarding/onboardingSkip.ts)
  AUTH_LINK_EMAIL_KEY, // The address a magic-link redemption is for (redemptionEmail.ts)
  FIRST_SEND_ADDRESS_KEY, // The first address this tab sent a link to (firstSendAddress.ts)
  FIRST_SEND_TURNED_DOWN_KEY, // Turned down: "Wrong address?" or a send elsewhere (same file)
  FIRST_SEND_SENT_TO_ANOTHER_KEY, // A send that went out stripped, which the door's mount strips on (same file)
  // The reload-surviving copy of `/auth/link`'s captured params
  // (authLinkParams.ts). It holds a live one-time TOKEN, so unlike the two
  // addresses above it is a credential: it belongs in this sweep, and it is
  // deliberately NOT in AUTH_HANDOVER_KEYS below -- a switch to a different
  // account has no business carrying the outgoing link's token across, and the
  // redemption that runs the switch already has the token in hand.
  AUTH_LINK_PARAMS_STASH_KEY,
  SIGNED_IN_CUE_KEY, // The "Signed in as" cue for a redemption nobody clicked for (signedInCue.ts)
  // The silent re-auth marker (auth.ts). Swept on sign-out AND on an account switch: a
  // redemption that lands at `/callback` within its 2 minutes would otherwise be treated as
  // the return leg of a `prompt=none` attempt and skip the post-auth ladder.
  SILENT_REAUTH_KEY,
  // "This sign-in was clicked for" (crossTabAuthSync/authFlowFlags.ts). Not carried across a
  // switch: the redemption that runs the switch stamps it again as it starts its sign-in.
  USER_INITIATED_SIGNIN_KEY,
  // NOT `signedOutByOtherTabSlot`'s key: AuthProvider spends it at mount
  // (`useSlotAtMountAndClear`), so a sign-out sweep would have nothing to remove, and keeping
  // it out means no future sweep on the signed-out tab can eat it before the reload shows it.
];
const SESSION_STORAGE_PREFIXES: string[] = [];

/**
 * AIDEV-NOTE [MEETBALL magic-link]: the keys that carry a sign-in ACROSS the
 * handover, and are therefore the one thing a mid-flight sweep must not touch.
 *
 * `clearOutgoingAccountStorage` below runs from `/auth/link` while a redemption
 * is in flight: the previous account's data has to go, but the destination, the
 * pending action and the pending-question claim the click commits belong to the
 * sign-in that is HAPPENING, not to the account being left. Without this
 * exclusion the switch would sweep away the values `/auth/link` hydrated moments
 * earlier out of the cross-tab mirror, and the visitor would land on /home with
 * their join, their RSVP or their question silently dropped -- the exact
 * silent-loss class this feature keeps producing.
 *
 * The two full sign-out sweeps do NOT pass this: there, the flow is over and the
 * values must die with it.
 */
const AUTH_HANDOVER_KEYS: ReadonlySet<string> = new Set<string>([
  'meetball_auth_intent',
  'meetball_redirect_path',
  'meetball_redirect_timestamp',
  // The pending-question claim -- the flag, the per-row secrets that ARE the claim now, and their
  // provenance bit. The secrets and the bit belong to the redemption in flight; the raw flag may
  // instead belong to the account being left, so the click drops it (the R13 note below). They are
  // carried together because the sweep runs in the same click as `/auth/link`'s commit, a flag
  // exempted without its secrets would survive able to name nothing, and one exempted without its
  // bit would be treated as this profile's own and re-mirrored by the next door entry. Derived from
  // the owning module's key list so a key added to the claim is carried by default, which is what
  // the other three are; the EXCLUSION is the part to argue about, and there is exactly one.
  //
  // AIDEV-NOTE [MEETBALL magic-link]: the DEFERRED marker was exempt here too, on the reasoning
  // that sweeping it would strand the rows it points at. In this same click that reasoning is
  // inverted: the three account-switch gates never offer the unnamed scope (Dexter, 2026-09-29),
  // and a refusal clears the marker outright -- so any marker still standing when this sweep runs
  // is one the gate WITHHELD. An earlier retire in this tab wrote it, possibly under the account
  // being left, or this click's own narrowing did (`keepPendingQuestionClaimEntriesFiledUnder`,
  // out of a raw flag no gate offered), and either way it is exactly the data this list exists to
  // drop. Left exempt, the incoming account's next magic-link sign-in in this tab is offered the
  // UNNAMED SCOPE -- `CARRIED_CLAIM_PHRASES.unnamed` in `AuthLinkLanding.tsx`, whose wording is
  // pinned by `gates a claim it cannot name, and says only what it knows` -- and Continue there
  // publishes, by the profile-wide device token, rows that may be the outgoing account's.
  //
  // AIDEV-NOTE: the RAW FLAG stays exempt and the click drops it (review 5330705557, R13). A flag
  // standing on a switch gate is the same fact as a withheld marker one retire earlier (the
  // incoming account's secrets claim would defer it into exactly that marker), and since a switch
  // gate never offers the unnamed scope, `handleConfirm` drops it on every switch. That squares
  // with the claim MIRROR, which `magicLinkMirrorKeysOfKind('intent')` below keeps on
  // purpose: the mirror holds NAMED entries only (its writer skips a flag-only tab, its hydrator
  // never arms the flag), so what it carries is named on a gate before it can publish, and it
  // cannot bring the flag back.
  ...PENDING_QUESTION_TAB_KEYS.filter(key => key !== PENDING_QUESTION_DEFERRED_KEY),
  // The address the in-flight redemption is for, so the retry from `/callback`
  // starts with the field filled. Written by `/auth/link`'s confirm handler in
  // the SAME click that runs this sweep on the three account-switch gates, so
  // without this entry the sweep would delete it microseconds after the write.
  AUTH_LINK_EMAIL_KEY,
  // The first-recipient binding, which MUST survive alongside the three things it guards:
  // the intent, the destination and the claim entries exempted above.
  //
  // AIDEV-NOTE [MEETBALL magic-link]: it was in `SESSION_STORAGE_KEYS` and not here,
  // so `clearOutgoingAccountStorage` -- which `/auth/link` runs on its three
  // account-switch gates -- deleted the binding while carefully preserving the
  // invitation token it binds. The next send from that tab then read "first
  // recipient" and re-addressed the capability. A guard swept away with its subject
  // left behind is worse than no guard: it reads as protection in the code.
  FIRST_SEND_ADDRESS_KEY,
  // ...and its turned-down mark and its send-elsewhere signal, which go wherever the
  // binding goes.
  FIRST_SEND_TURNED_DOWN_KEY,
  FIRST_SEND_SENT_TO_ANOTHER_KEY,
  // The "Signed in as" cue (signedInCue.ts). Every gate that runs this sweep and does not
  // name the signed-in account (`identity_timeout`, `confirm_unknown_identity`) keeps the cue
  // for the redemption it starts in the same click, so the sweep must not take it first.
  // `switch_account` does not keep it, and its sign-in clears it as it starts.
  SIGNED_IN_CUE_KEY,
  // AIDEV-NOTE [MEETBALL magic-link continuation]: the continuation list
  // (`mb.magicLink.continuation`) is the ONE mirror this set excludes, and the
  // exclusion is the fix for id 4015057883. This entry used to spread every
  // mirror, with a note arguing that an outgoing account's leftover record
  // "can only ever claim a token minted for the outgoing account's own
  // address, which the incoming account's identity gates already reject".
  // That sentence was false. A continuation record is a BEARER capability with
  // no address in it at all (§3.2 keeps the address out of the record
  // deliberately), and the path that spends it is not `/auth/link`: `AuthDoor`
  // adopts every unexpired record this browser PROFILE holds (see the
  // "WHAT IS ADOPTED" note at its mount read), lets an approved one win, and
  // redeems it with the `login_hint` the claim hands back. None of
  // `/auth/link`'s identity gates are on that path. So preserving the outgoing
  // account's pending records through a switch leaves a way back into the
  // account the visitor has just handed the profile away from, for the life of
  // the record's TTL.
  //
  // THIS EXCLUSION IS NECESSARY AND NOT SUFFICIENT, and the sentence above used
  // to stop here as though it were. Excluding the key only decides what the two
  // storage WALKS do, and both walks are `globalThis.localStorage` /
  // `globalThis.sessionStorage`. On native the records are in Capacitor
  // Preferences, which no walk in this file reaches, so the outgoing account's
  // pairs survived the switch on exactly the platform where they matter most.
  // `clearOutgoingAccountStorage` now carries the native leg explicitly; a test
  // that only asserts `localStorage.getItem(key) === null` passes either way and
  // does not cover it at all.
  //
  // What that costs, accepted: a record created moments before a switch is
  // abandoned, so a door tab left open in this profile stops polling. It costs
  // the redemption in flight nothing -- this sweep runs on the last line
  // before `signIn()` navigates to Logto (`handleConfirm` in
  // `AuthLinkLanding.tsx`), the redemption carries the token from the URL
  // rather than from a record, and `landingDecision`'s same-browser lookup
  // already ran at bootstrap.
  //
  // Every other mirror stays: they are the destination and the pending action
  // belonging to the sign-in that is HAPPENING, which is what this set is for.
  //
  // AIDEV-NOTE [MEETBALL magic-link continuation]: id 4074238784. Selected by
  // KIND, not by filtering one name out. The line here used to say "a future
  // seventh mirror is preserved by construction, as before", which is the right
  // default for an intent note and exactly wrong for a bearer capability -- the
  // thing this very filter exists to handle. A new key now has to declare
  // what it is in `MAGIC_LINK_MIRROR_KINDS` or it does not compile, and that
  // declaration is what decides this set.
  ...magicLinkMirrorKeysOfKind('intent'),
]);

const EMPTY_SKIP: ReadonlySet<string> = new Set<string>();

/**
 * Remove localStorage keys, optionally including Logto tokens
 * @param includeLogto - If true, also removes Logto SDK storage (default: true)
 * @param skip - Exact keys to leave in place (see AUTH_HANDOVER_KEYS). Matched via
 *   `skip.has(key)` only: a key that matches one of the sweep's prefixes is skipped
 *   only when its own full key is also listed in `skip`.
 */
const removeLocalStorageKeys = (
  includeLogto = true,
  skip: ReadonlySet<string> = EMPTY_SKIP,
): void => {
  const chatRelatedPrefixes = [
    CACHE_CONFIG.STORAGE_KEYS.MESSAGES,
    CACHE_CONFIG.STORAGE_KEYS.ROOMS,
    CACHE_CONFIG.STORAGE_KEYS.LAST_FETCH,
    CACHE_CONFIG.STORAGE_KEYS.USER_PREFERENCES,
  ];

  // Combine prefixes - only include Logto if requested
  const allPrefixes = includeLogto
    ? [...LOCAL_STORAGE_PREFIXES, ...chatRelatedPrefixes, ...LOGTO_STORAGE_PREFIXES]
    : [...LOCAL_STORAGE_PREFIXES, ...chatRelatedPrefixes];

  // One membership for both the storage walk and the mirror (values defineStoredValue
  // couldn't persist live in its mirror, out of the walk's reach).
  const swept = (key: string): boolean =>
    !skip.has(key) &&
    (LOCAL_STORAGE_KEYS.includes(key) || allPrefixes.some(p => key.startsWith(p)));

  forgetUnpersisted('local', swept);
  if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) {
    return;
  }

  try {
    const storage = globalThis.localStorage;
    for (let index = storage.length - 1; index >= 0; index -= 1) {
      const key = storage.key(index);
      if (key !== null && swept(key)) {
        storage.removeItem(key);
      }
    }
  } catch (error) {
    logger.error('Failed to clear localStorage during logout:', error);
  }
};

/**
 * Remove sessionStorage keys, optionally including Logto tokens
 * @param includeLogto - If true, also removes Logto SDK storage (default: true)
 * @param skip - Exact keys to leave in place; same matching as `removeLocalStorageKeys`.
 */
const removeSessionStorageKeys = (
  includeLogto = true,
  skip: ReadonlySet<string> = EMPTY_SKIP,
): void => {
  // Combine prefixes - only include Logto if requested
  const allPrefixes = includeLogto
    ? [...SESSION_STORAGE_PREFIXES, ...LOGTO_STORAGE_PREFIXES]
    : SESSION_STORAGE_PREFIXES;

  const swept = (key: string): boolean =>
    !skip.has(key) &&
    (SESSION_STORAGE_KEYS.includes(key) || allPrefixes.some(p => key.startsWith(p)));

  forgetUnpersisted('session', swept);
  if (typeof globalThis === 'undefined' || !('sessionStorage' in globalThis)) {
    return;
  }

  try {
    const storage = globalThis.sessionStorage;
    for (let index = storage.length - 1; index >= 0; index -= 1) {
      const key = storage.key(index);
      if (key !== null && swept(key)) {
        storage.removeItem(key);
      }
    }
  } catch (error) {
    logger.error('Failed to clear sessionStorage during logout:', error);
  }
};

/**
 * Clear non-Logto user storage (app state, caches, drafts)
 *
 * AIDEV-NOTE: Use this BEFORE calling logtoSignOut() to clear app state
 * while preserving Logto tokens needed for server-side session invalidation.
 */
export const clearNonLogtoStorage = (): void => {
  if (typeof globalThis === 'undefined') {
    return;
  }

  try {
    MessageCache.clearAll();
  } catch (error) {
    logger.error('Failed to clear chat message cache during logout:', error);
  }

  try {
    OfflineMessageQueue.clear();
  } catch (error) {
    logger.error('Failed to clear offline message queue during logout:', error);
  }

  // Clear the overlay intent channel through the store: the key-walk below only
  // reaches sessionStorage, not the module's in-memory copy or its subscribers.
  clearIntents();

  // Live poll guest tokens (TECH-867): stored per code and, when storage refused one, in memory, which no key
  // walk reaches. The next person on this device must not answer as this guest.
  forgetAllGuestTokens();
  forgetAllTakeovers();

  // Clear auth intent from all storage layers (sessionStorage + native Preferences)
  clearAuthIntent();

  // Clear storage but preserve Logto tokens
  removeLocalStorageKeys(false);
  removeSessionStorageKeys(false);
};

/**
 * Clear the OUTGOING account's local data while a sign-in for a different
 * account is already in flight.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: `/auth/link`'s account-switch gates are the
 * only caller, and the shape of the problem is specific to them. That screen
 * deliberately does NOT call `signOut()` -- signOut ends at Logto's end-session
 * endpoint via a full-page redirect, which unloads the document and destroys the
 * module singleton holding the one-time token, so a sign-out-then-sign-in
 * sequence can never reach the sign-in. What it used to do instead was
 * `setCurrentUser(null)` + `clearCachedAuth()`, which covers exactly three keys
 * under `auth-storage` and nothing else.
 *
 * So on a shared laptop, after A was signed in and B opened their magic link and
 * tapped "Switch to b***@...", B's client read A's `MessageCache` (keyed by room
 * id with NO user-id component), flushed A's `OfflineMessageQueue` under B's
 * access token, and inherited A's `meetball-app-store` and question draft. The
 * narrowest clear in the codebase was running on the three gates that exist
 * precisely BECAUSE somebody else may be signed in.
 *
 * This is the same sweep as `clearNonLogtoStorage` -- it does not navigate and
 * does not touch the Logto keys the redemption still needs -- minus
 * `AUTH_HANDOVER_KEYS`, because the destination and pending action belong to the
 * sign-in that is happening rather than to the account being left. `signOut`'s
 * own two sweeps are untouched.
 */
export const clearOutgoingAccountStorage = (): void => {
  if (typeof globalThis === 'undefined') {
    return;
  }

  try {
    MessageCache.clearAll();
  } catch (error) {
    logger.error('Failed to clear chat message cache during account switch:', error);
  }

  try {
    OfflineMessageQueue.clear();
  } catch (error) {
    logger.error('Failed to clear offline message queue during account switch:', error);
  }

  // The outgoing account's queued overlays belong to the account being left,
  // same as its chat state above; the intent channel is not in AUTH_HANDOVER_KEYS.
  clearIntents();

  // The outgoing account's live poll takeovers (memory half; the walk below takes the stored keys).
  forgetAllTakeovers();

  // Deliberately NOT clearAuthIntent(): that clears every layer including the
  // intent this redemption is carrying. AUTH_HANDOVER_KEYS holds the same line.
  removeLocalStorageKeys(false, AUTH_HANDOVER_KEYS);
  removeSessionStorageKeys(false, AUTH_HANDOVER_KEYS);

  // The NATIVE half of the continuation exclusion above. Leaving
  // MAGIC_LINK_MIRROR_KEYS.continuation out of AUTH_HANDOVER_KEYS is what makes
  // the two walks destroy it -- but that is a localStorage fact, and both walks
  // are `globalThis.localStorage` / `globalThis.sessionStorage` only. On native the
  // records are in Capacitor Preferences, which nothing in this file reaches, so
  // the outgoing account's bearer pairs survived the switch to their TTL.
  //
  // `void`, not awaited: this function is synchronous and stays that way, which
  // is the same seam and the same discipline `clearAuthIntent()` above uses for
  // its own native mirror.
  void clearContinuationOnAccountSwitch();
};

/**
 * Clear all user storage including Logto tokens
 *
 * AIDEV-NOTE: Use this AFTER logtoSignOut() completes to ensure
 * Logto SDK had access to tokens for server-side session invalidation.
 */
export const clearUserStorage = (): void => {
  if (typeof globalThis === 'undefined') {
    return;
  }

  try {
    MessageCache.clearAll();
  } catch (error) {
    logger.error('Failed to clear chat message cache during logout:', error);
  }

  try {
    OfflineMessageQueue.clear();
  } catch (error) {
    logger.error('Failed to clear offline message queue during logout:', error);
  }

  // Clear the overlay intent channel through the store: the key-walk below only
  // reaches sessionStorage, not the module's in-memory copy or its subscribers.
  clearIntents();

  // Live poll guest tokens (TECH-867): stored per code and, when storage refused one, in memory, which no key
  // walk reaches. The next person on this device must not answer as this guest.
  forgetAllGuestTokens();
  forgetAllTakeovers();

  // Clear all storage including Logto tokens
  removeLocalStorageKeys(true);
  removeSessionStorageKeys(true);
};

interface PersistedDraftState {
  state?: {
    draft?: QuestionDraft | null;
  };
}

const draftHasContent = (draft: QuestionDraft): boolean => {
  if (draft.title.trim()) {
    return true;
  }

  if (draft.description.trim()) {
    return true;
  }

  return false;
};

export const hasDraftInStorage = (): boolean => {
  if (typeof globalThis === 'undefined' || !('sessionStorage' in globalThis)) {
    return false;
  }

  try {
    const raw = globalThis.sessionStorage.getItem('create-question-draft');
    if (!raw) {
      return false;
    }

    const parsed = JSON.parse(raw) as PersistedDraftState;
    const draft = parsed.state?.draft;

    if (!draft) {
      return false;
    }

    return draftHasContent(draft);
  } catch (error) {
    logger.warn('Failed to parse draft from sessionStorage during logout check:', error);
    return false;
  }
};

/**
 * Clear all service worker caches including Workbox precache and runtime caches
 * @returns Promise with result details
 */
export const clearPWACaches = async (): Promise<{
  success: boolean;
  cachesCleared: number;
  workersUnregistered: number;
  error?: string;
}> => {
  const result = {
    success: true,
    cachesCleared: 0,
    workersUnregistered: 0,
    error: undefined as string | undefined,
  };

  try {
    // Clear all caches with timeout
    if ('caches' in window) {
      const clearCachesPromise = async () => {
        const cacheNames = await caches.keys();
        const count = cacheNames.length;

        if (count > 0) {
          logger.info('Clearing service worker caches:', cacheNames);
          await Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)));
          logger.info('All service worker caches cleared');
        }

        return count;
      };

      // Race against 3 second timeout with cleanup
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      const timeoutPromise = new Promise<number>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error('Cache clearing timeout'));
        }, 3000);
      });

      try {
        const cachesCleared = await Promise.race([clearCachesPromise(), timeoutPromise]);
        if (timeoutId) clearTimeout(timeoutId); // Clear timeout if operation completed successfully
        result.cachesCleared = cachesCleared;
      } catch (error) {
        if (timeoutId) clearTimeout(timeoutId); // Clear timeout even on error
        throw error;
      }
    }

    // Unregister all service workers with timeout
    if ('serviceWorker' in navigator) {
      const unregisterPromise = async () => {
        const registrations = await navigator.serviceWorker.getRegistrations();
        const count = registrations.length;

        if (count > 0) {
          logger.info('Unregistering service workers:', count);
          await Promise.all(registrations.map(registration => registration.unregister()));
          logger.info('All service workers unregistered');
        }

        return count;
      };

      // Race against 3 second timeout with cleanup
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      const timeoutPromise = new Promise<number>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error('Service worker unregister timeout'));
        }, 3000);
      });

      try {
        const workersUnregistered = await Promise.race([unregisterPromise(), timeoutPromise]);
        if (timeoutId) clearTimeout(timeoutId); // Clear timeout if operation completed successfully
        result.workersUnregistered = workersUnregistered;
      } catch (error) {
        if (timeoutId) clearTimeout(timeoutId); // Clear timeout even on error
        throw error;
      }
    }
  } catch (error) {
    result.success = false;
    result.error = error instanceof Error ? error.message : 'Unknown error';
    logger.error('Failed to clear PWA caches:', error);
  }

  return result;
};
